/**
 * WebServerProvider - Sends log entries to backend in batches
 *
 * Buffers up to MAX_BUFFER entries and flushes every FLUSH_INTERVAL_MS.
 * Uses navigator.sendBeacon on page unload for reliable delivery.
 */

import type { WebLogProvider, WebLogEntry } from '../types';

const MAX_BUFFER = 20;
const FLUSH_INTERVAL_MS = 5000;

export class WebServerProvider implements WebLogProvider {
  name = 'web-server' as const;
  private buffer: WebLogEntry[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private endpoint: string;
  private getAuthToken: (() => string | null) | null;
  private onBeforeUnload: (() => void) | null = null;

  constructor(apiBaseUrl: string, getAuthToken?: () => string | null) {
    this.endpoint = `${apiBaseUrl}/console/client-logs`;
    this.getAuthToken = getAuthToken || null;
    this.startFlushTimer();
    this.setupUnloadHandler();
  }

  log(entry: WebLogEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length >= MAX_BUFFER) {
      this.flushNow();
    }
  }

  async flush(): Promise<void> {
    await this.flushNow();
  }

  destroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.onBeforeUnload) {
      window.removeEventListener('beforeunload', this.onBeforeUnload);
      this.onBeforeUnload = null;
    }
    // Final flush with sendBeacon
    this.sendBeacon();
  }

  private startFlushTimer(): void {
    this.timer = setInterval(() => {
      this.flushNow();
    }, FLUSH_INTERVAL_MS);
  }

  private setupUnloadHandler(): void {
    this.onBeforeUnload = () => {
      this.sendBeacon();
    };
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  /** Use sendBeacon for page unload (no custom headers possible) */
  private sendBeacon(): void {
    if (this.buffer.length === 0) return;
    const logs = this.buffer.splice(0, 50);
    try {
      const blob = new Blob([JSON.stringify({ logs })], { type: 'application/json' });
      navigator.sendBeacon(this.endpoint, blob);
    } catch {
      // Best-effort on unload
    }
  }

  /** Use fetch for regular flushes (supports auth headers) */
  private async flushNow(): Promise<void> {
    if (this.buffer.length === 0) return;
    const logs = this.buffer.splice(0, 50);

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = this.getAuthToken?.();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(this.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ logs }),
        keepalive: true,
      });
    } catch {
      // Re-buffer on failure (up to limit to prevent unbounded growth)
      if (this.buffer.length < MAX_BUFFER * 3) {
        this.buffer.unshift(...logs);
      }
    }
  }
}
